import Swal from 'sweetalert2';

/**
 * Edição de número para um que já pertence a outro contato. O servidor só responde
 * `"Código já em uso"`, sem dizer de quem; por isso as telas consultam o dono do
 * número ANTES de salvar (`contatoStore.buscarOutroContatoComCodigo`) e mostram
 * este aviso no lugar do PATCH.
 */
export interface ContatoDuplicado {
    id: string;
    nome: string | null;
    codigo: string;
}

/** Só vale consultar quando o número mudou de fato (formatação não conta). */
export function numeroMudou(codigoAtual: string | null | undefined, codigoNovo: string | null | undefined): boolean {
    const digitos = (s: string | null | undefined) => (s ?? '').replace(/\D/g, '');
    return !!digitos(codigoNovo) && digitos(codigoAtual) !== digitos(codigoNovo);
}

export const escapar = (s: string) => s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));

/**
 * Explica que o número já é de outro contato e pergunta se o operador quer ir até
 * ele. Resolve `true` quando o operador escolheu ir para o contato.
 */
export async function avisarContatoDuplicado(dup: ContatoDuplicado): Promise<boolean> {
    const nome = dup.nome?.trim();
    const quem = nome ? `ao contato <strong>${escapar(nome)}</strong>` : 'a outro contato';
    const result = await Swal.fire({
        title: 'Número já cadastrado',
        icon: 'warning',
        html: `O número <strong>${escapar(dup.codigo)}</strong> já pertence ${quem}.<br>Deseja abrir esse contato?`,
        showCancelButton: true,
        confirmButtonText: 'Abrir contato',
        cancelButtonText: 'Voltar',
        // O popup herda o fundo do tema (`--bs-light`), mas o texto do SweetAlert
        // tem cor fixa (#545454) e some no modo escuro; `text-body` segue o tema.
        customClass: { title: 'text-body', htmlContainer: 'text-body' },
    });
    return result.isConfirmed;
}

/**
 * Vai até o dono do número pelo MESMO fluxo do clique num número dentro de uma
 * mensagem: foca o atendimento aberto com ele ou oferece abrir um.
 */
export async function abrirConversaDoContatoDuplicado(dup: ContatoDuplicado) {
    // Import tardio: o chatStore puxa o app inteiro, e este util é testado isolado.
    const { useChatStore } = await import('@/stores/app/chatStore');
    return useChatStore().handleClickOnTextWithAction('phone', dup.codigo, dup.nome ?? undefined);
}
